import { memo, useCallback, useMemo } from 'react';

import { type Board, type ScopedPermissions } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { LibraryHeaderUploadButton } from '~/components/LibraryBeta/LibraryHeaderUploadButton';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { CreateDropdownButton } from '~/components/Shared/HeaderBar/CreateDropdownButton';
import { getLibraryCreateMenuOptions } from '~/components/Shared/HeaderBar/getDescriptiveCreateMenuOptions';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useLibraryHeaderUploadMenuOptions } from '~/hooks/useLibraryHeaderUploadActions';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

export type LibraryHeaderCreateButtonProps = {
  'data-testid': string;
  library: NonNullable<Board['library']>;
  libraryPermissions: ScopedPermissions | undefined;
  workspacePermissions: ScopedPermissions | undefined;
};

export const LibraryHeaderCreateButton = memo(
  ({
    'data-testid': dataTestId,
    library,
    libraryPermissions,
    workspacePermissions,
  }: LibraryHeaderCreateButtonProps) => {
    const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
    const { openDraftCanvas } = useDraftCanvas();
    const [showCreateBoardModal] = useAirModal(CreateBoardModal);

    const handleCreateBoard = useCallback(() => {
      showCreateBoardModal({
        initialLibrary: library,
        trackLocation: 'library',
      });
    }, [library, showCreateBoardModal]);

    const handleCreateCanvas = useCallback(() => {
      openDraftCanvas({
        analyticsContext: { source: 'library', library_id: library.id },
      });
    }, [library.id, openDraftCanvas]);

    const { uploadMenuOptions } = useLibraryHeaderUploadMenuOptions({ permissions: libraryPermissions });

    const createBoard = canCreateBoard(libraryPermissions) ? handleCreateBoard : undefined;
    const createCanvas =
      generativeCanvasEnabled && canCreateCanvasInWorkspace(workspacePermissions) ? handleCreateCanvas : undefined;

    const createMenuOptions = useMemo(
      () =>
        getLibraryCreateMenuOptions({
          onCreateBoard: createBoard,
          onCreateCanvas: createCanvas,
        }),
      [createBoard, createCanvas],
    );

    if (!uploadMenuOptions.length && !createMenuOptions.length) {
      return null;
    }

    return (
      <div className="flex shrink-0 items-center gap-1">
        <LibraryHeaderUploadButton options={uploadMenuOptions} />
        <CreateDropdownButton
          color="gray"
          data-testid={dataTestId}
          label="Create"
          menuClassName="w-80"
          options={createMenuOptions}
        />
      </div>
    );
  },
);

LibraryHeaderCreateButton.displayName = 'LibraryHeaderCreateButton';
